<script setup>
import { computed, ref, onMounted, onBeforeUnmount } from 'vue'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'

import Avatar from '@/components/Avatar.vue'
import { useAlert } from '@/composables/useToast'
import { useSoundSetting } from '@/composables/useUnreadAlerts'

const store = useStore()
const route = useRoute()
const { t } = useI18n()

const { soundEnabled, toggleSound } = useSoundSetting()

// Unread messages waiting in the dialog list
const unreadTotal = computed(() => store.getters['stats/getUnreadTotal'])
// An administrator does not read every thread, so their badge is the queue:
// how many clients wait for an operator. Grey while it is small, then louder
const queuedTotal = computed(
  () => store.getters['stats/getStats']?.state_counts?.queued ?? 0
)
const queueTone = computed(() => {
  if (queuedTotal.value > 20) return 'text-white bg-n-ruby-9'
  if (queuedTotal.value >= 10) return 'text-white bg-n-amber-9'
  return 'text-n-slate-12 bg-n-alpha-2'
})

const currentUser = computed(() => store.getters['auth/getCurrentUser'])
const currentAccount = computed(() => currentUser.value?.accounts?.[0])
const isAdministrator = computed(
  () => currentUser.value?.role === 'administrator'
)
const hasPermission = computed(() => store.getters['auth/hasPermission'])
const canSeeAll = computed(() => hasPermission.value('conversations_all'))
const canManageInboxes = computed(() => hasPermission.value('inboxes_manage'))
const canManageBlocklist = computed(() =>
  hasPermission.value('blocklist_manage')
)
// The blocklist is the only place a request shows up, so the header carries the count
const pendingBlockRequests = computed(
  () => store.getters['stats/getStats']?.block_requests_pending || 0
)
const isConversationsActive = computed(() => route.path === '/')
const isMonitoringActive = computed(() => route.path.startsWith('/monitoring'))
const isDashboardsActive = computed(() => route.path.startsWith('/dashboards'))
const isSettingsActive = computed(() => route.path.startsWith('/settings'))

const settingsItems = computed(() =>
  [
    {
      to: '/settings/agents',
      labelKey: 'SETTINGS.NAV.AGENTS',
      icon: 'i-lucide-square-user',
      visible: isAdministrator.value,
    },
    {
      to: '/settings/directory',
      labelKey: 'SETTINGS.NAV.DIRECTORY',
      icon: 'i-lucide-book-marked',
      visible: true,
    },
    {
      to: '/settings/inboxes',
      labelKey: 'SETTINGS.NAV.INBOXES',
      icon: 'i-lucide-inbox',
      visible: canManageInboxes.value,
    },
    {
      to: '/settings/blocklist',
      labelKey: 'SETTINGS.NAV.BLOCKLIST',
      icon: 'i-lucide-ban',
      visible: canManageBlocklist.value,
      badge: pendingBlockRequests.value,
    },
  ].filter(item => item.visible)
)

const AVAILABILITY_OPTIONS = [
  { value: 'online', labelKey: 'AVAILABILITY.ONLINE', dotClass: 'bg-n-teal-9' },
  { value: 'busy', labelKey: 'AVAILABILITY.BUSY', dotClass: 'bg-n-amber-9' },
  {
    value: 'offline',
    labelKey: 'AVAILABILITY.OFFLINE',
    dotClass: 'bg-n-slate-9',
  },
]

const availability = computed(
  () => currentUser.value?.availability || 'offline'
)
const currentAvailabilityOption = computed(
  () =>
    AVAILABILITY_OPTIONS.find(option => option.value === availability.value) ||
    AVAILABILITY_OPTIONS[0]
)

const savingAvailability = ref(false)

// Time on shift, ticking next to the status (spec 3.1.5)
const now = ref(Date.now())
let clockTimer = null

const shiftDuration = computed(() => {
  const startedAt = currentUser.value?.shift_started_at
  if (!startedAt || availability.value === 'offline') return null

  const totalMinutes = Math.max(
    0,
    Math.floor((now.value - new Date(startedAt).getTime()) / 60000)
  )
  return `${Math.floor(totalMinutes / 60)}:${String(totalMinutes % 60).padStart(2, '0')}`
})

const setAvailability = async value => {
  statusMenuOpen.value = false
  if (value === availability.value || savingAvailability.value) return
  savingAvailability.value = true
  try {
    await store.dispatch('auth/updateProfile', { availability: value })
  } catch (error) {
    useAlert(t('AVAILABILITY.ERROR'))
  } finally {
    savingAvailability.value = false
  }
}

// One dropdown at a time; any click outside the bar closes them all
const settingsMenuOpen = ref(false)
const statusMenuOpen = ref(false)
const profileMenuOpen = ref(false)
const barRef = ref(null)

const closeMenus = () => {
  settingsMenuOpen.value = false
  statusMenuOpen.value = false
  profileMenuOpen.value = false
}

const toggleSettingsMenu = () => {
  const next = !settingsMenuOpen.value
  closeMenus()
  settingsMenuOpen.value = next
}

const toggleStatusMenu = () => {
  const next = !statusMenuOpen.value
  closeMenus()
  statusMenuOpen.value = next
}

const toggleProfileMenu = () => {
  const next = !profileMenuOpen.value
  closeMenus()
  profileMenuOpen.value = next
}

const onDocumentClick = event => {
  if (barRef.value && !barRef.value.contains(event.target)) closeMenus()
}

const onLogout = () => {
  closeMenus()
  store.dispatch('auth/logout')
}

onMounted(() => {
  document.addEventListener('click', onDocumentClick)
  clockTimer = setInterval(() => {
    now.value = Date.now()
  }, 30000)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', onDocumentClick)
  if (clockTimer) clearInterval(clockTimer)
})
</script>

<template>
  <header
    ref="barRef"
    class="flex items-center gap-4 h-12 px-4 shrink-0 text-sm bg-n-background border-b border-n-weak"
  >
    <div class="flex items-center gap-2 min-w-0 shrink-0">
      <div
        class="grid shrink-0 place-content-center size-6 rounded-md bg-n-brand"
      >
        <span class="i-lucide-messages-square size-4 text-white" />
      </div>
      <span
        class="hidden sm:block text-sm font-medium leading-5 text-n-slate-12 truncate max-w-48"
      >
        {{ currentAccount?.name }}
      </span>
    </div>

    <nav class="flex items-center gap-1 min-w-0">
      <router-link
        to="/"
        class="flex items-center gap-2 px-3 h-8 rounded-lg"
        :class="
          isConversationsActive
            ? 'bg-n-alpha-2 text-n-slate-12 font-medium'
            : 'text-n-slate-11 hover:bg-n-alpha-2'
        "
        @click="closeMenus"
      >
        <span class="i-lucide-message-circle size-4" />
        <span class="truncate text-body-main">
          {{ $t('NAV.CONVERSATIONS') }}
        </span>
        <span
          v-if="isAdministrator && queuedTotal"
          class="grid px-1.5 rounded-full place-content-center min-w-5 h-5 text-xs font-medium"
          :class="queueTone"
          :title="$t('NAV.QUEUE_BADGE')"
        >
          {{ queuedTotal > 99 ? '99+' : queuedTotal }}
        </span>
        <span
          v-else-if="unreadTotal && !isAdministrator"
          class="grid px-1.5 rounded-full place-content-center min-w-5 h-5 text-xs font-medium text-white bg-n-ruby-9"
        >
          {{ unreadTotal > 99 ? '99+' : unreadTotal }}
        </span>
      </router-link>

      <router-link
        v-if="canSeeAll"
        to="/monitoring"
        class="flex items-center gap-2 px-3 h-8 rounded-lg"
        :class="
          isMonitoringActive
            ? 'bg-n-alpha-2 text-n-slate-12 font-medium'
            : 'text-n-slate-11 hover:bg-n-alpha-2'
        "
        @click="closeMenus"
      >
        <span class="i-lucide-activity size-4" />
        <span class="truncate text-body-main">
          {{ $t('NAV.MONITORING') }}
        </span>
      </router-link>

      <router-link
        v-if="canSeeAll"
        to="/dashboards"
        class="flex items-center gap-2 px-3 h-8 rounded-lg"
        :class="
          isDashboardsActive
            ? 'bg-n-alpha-2 text-n-slate-12 font-medium'
            : 'text-n-slate-11 hover:bg-n-alpha-2'
        "
        @click="closeMenus"
      >
        <span class="i-lucide-chart-column size-4" />
        <span class="truncate text-body-main">
          {{ $t('NAV.DASHBOARDS') }}
        </span>
      </router-link>

      <div class="relative">
        <button
          type="button"
          class="flex items-center gap-2 px-3 h-8 rounded-lg"
          :class="
            isSettingsActive
              ? 'bg-n-alpha-2 text-n-slate-12 font-medium'
              : 'text-n-slate-11 hover:bg-n-alpha-2'
          "
          :aria-expanded="settingsMenuOpen"
          @click="toggleSettingsMenu"
        >
          <span class="i-lucide-bolt size-4" />
          <span class="truncate text-body-main">
            {{ $t('NAV.SETTINGS') }}
          </span>
          <span
            v-if="pendingBlockRequests"
            class="grid px-1.5 min-w-5 h-5 text-xs font-medium text-white rounded-full shrink-0 place-content-center bg-n-ruby-9"
          >
            {{ pendingBlockRequests }}
          </span>
          <span
            class="size-3 shrink-0"
            :class="
              settingsMenuOpen ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'
            "
          />
        </button>
        <div
          v-if="settingsMenuOpen"
          class="absolute left-0 z-50 p-1 mt-1 top-full min-w-48 rounded-xl border border-n-weak bg-n-alpha-3 backdrop-blur-[100px] shadow-sm"
        >
          <router-link
            v-for="item in settingsItems"
            :key="item.to"
            :to="item.to"
            class="flex items-center gap-2 w-full p-2 text-sm rounded-lg text-n-slate-12 hover:bg-n-alpha-2"
            @click="closeMenus"
          >
            <span class="size-4" :class="item.icon" />
            {{ $t(item.labelKey) }}
            <span
              v-if="item.badge"
              class="grid px-1.5 ml-auto min-w-5 h-5 text-xs font-medium text-white rounded-full shrink-0 place-content-center bg-n-ruby-9"
            >
              {{ item.badge }}
            </span>
          </router-link>
        </div>
      </div>
    </nav>

    <div class="flex items-center gap-2 ml-auto shrink-0">
      <button
        v-tooltip.bottom="
          soundEnabled
            ? $t('NOTIFICATIONS.SOUND_OFF')
            : $t('NOTIFICATIONS.SOUND_ON')
        "
        type="button"
        class="grid border-0 rounded-lg cursor-pointer place-content-center size-8 bg-transparent text-n-slate-11 hover:bg-n-alpha-2"
        :aria-label="
          soundEnabled
            ? $t('NOTIFICATIONS.SOUND_OFF')
            : $t('NOTIFICATIONS.SOUND_ON')
        "
        :aria-pressed="!soundEnabled"
        @click="toggleSound"
      >
        <span
          class="size-4"
          :class="soundEnabled ? 'i-lucide-volume-2' : 'i-lucide-volume-x'"
        />
      </button>
      <div class="relative">
        <button
          type="button"
          class="flex items-center gap-2 h-8 px-3 rounded-lg text-n-slate-12 hover:bg-n-alpha-2"
          :aria-expanded="statusMenuOpen"
          @click="toggleStatusMenu"
        >
          <span
            class="size-2 rounded-full shrink-0"
            :class="currentAvailabilityOption.dotClass"
          />
          <span class="hidden sm:block truncate text-body-main">
            {{ $t(currentAvailabilityOption.labelKey) }}
          </span>
          <span
            v-if="shiftDuration"
            v-tooltip.bottom="$t('CONVERSATIONS.STATS.SHIFT_TITLE')"
            class="hidden sm:block text-xs text-n-slate-11"
          >
            {{ shiftDuration }}
          </span>
          <span
            class="size-3 shrink-0 text-n-slate-11"
            :class="
              statusMenuOpen ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'
            "
          />
        </button>
        <div
          v-if="statusMenuOpen"
          class="absolute right-0 z-50 p-1 mt-1 top-full min-w-40 rounded-xl border border-n-weak bg-n-alpha-3 backdrop-blur-[100px] shadow-sm"
        >
          <button
            v-for="option in AVAILABILITY_OPTIONS"
            :key="option.value"
            type="button"
            class="flex items-center w-full gap-2 p-2 text-sm text-left border-0 rounded-lg text-n-slate-12 hover:bg-n-alpha-2"
            :disabled="savingAvailability"
            @click="setAvailability(option.value)"
          >
            <span
              class="size-2 rounded-full shrink-0"
              :class="option.dotClass"
            />
            {{ $t(option.labelKey) }}
            <span
              v-if="availability === option.value"
              class="i-lucide-check size-3.5 ml-auto text-n-brand"
            />
          </button>
        </div>
      </div>

      <div class="relative">
        <button
          type="button"
          class="flex items-center gap-2 p-1 rounded-lg min-w-0 hover:bg-n-alpha-1"
          :class="{ 'bg-n-alpha-1': profileMenuOpen }"
          @click="toggleProfileMenu"
        >
          <Avatar
            v-if="currentUser"
            :name="currentUser.available_name || currentUser.name"
            :size="28"
            :status="availability"
            :src="currentUser.avatar_url"
            logo-fallback
          />
        </button>
        <div
          v-if="profileMenuOpen"
          class="absolute right-0 z-50 mt-1 top-full w-72"
        >
          <ul
            class="grid gap-1 p-2 m-0 text-sm list-none bg-n-alpha-3 backdrop-blur-[100px] border border-n-weak rounded-xl shadow-sm"
          >
            <li class="flex items-center gap-2 p-2 min-w-0">
              <Avatar
                v-if="currentUser"
                :name="currentUser.available_name || currentUser.name"
                :size="32"
                :status="availability"
                :src="currentUser.avatar_url"
                logo-fallback
              />
              <div class="min-w-0">
                <div
                  class="text-sm font-medium leading-4 truncate text-n-slate-12"
                >
                  {{ currentUser?.available_name }}
                </div>
                <div class="text-xs truncate text-n-slate-11">
                  {{ currentUser?.email }}
                </div>
              </div>
            </li>
            <li><div class="h-px bg-n-weak" /></li>
            <li>
              <router-link
                to="/profile"
                class="flex items-center w-full gap-3 p-2 text-sm border-0 rounded-lg text-n-slate-12 hover:bg-n-alpha-2"
                @click="closeMenus"
              >
                <span class="i-lucide-user-pen size-4 text-n-slate-11" />
                {{ $t('PROFILE_MENU.SETTINGS') }}
              </router-link>
            </li>
            <li>
              <button
                type="button"
                class="flex items-center w-full gap-3 p-2 text-sm text-left border-0 rounded-lg text-n-slate-12 hover:bg-n-alpha-2"
                @click="onLogout"
              >
                <span class="i-lucide-power size-4 text-n-slate-11" />
                {{ $t('PROFILE_MENU.LOGOUT') }}
              </button>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </header>
</template>
